import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import { useMemo } from 'react';
// @mui
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
// locales
import { useLocales } from 'src/components/utils/locales';
// components
import { useSettingsContext } from 'src/components/ui/settings';
// system
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './theme-styles';
// options
import { presets } from './options/presets';
import { darkMode } from './options/dark-mode';
import { contrast } from './options/contrast';
import RTL, { direction } from './options/right-to-left';

// ----------------------------------------------------------------------

export function ThemeProvider({ children }) {
  const { currentLang } = useLocales();
  const settings = useSettingsContext();

  // Memoize theme options to prevent unnecessary recalculations
  const themeOptions = useMemo(() => {
    const darkModeOption = darkMode(settings.themeMode);
    const presetsOption = presets(settings.themeColorPresets);
    const contrastOption = contrast(
      settings.themeContrast === 'bold',
      settings.themeMode
    );
    const directionOption = direction(settings.themeDirection);

    const baseOption = {
      palette: palette('light'),
      shadows: shadows('light'),
      customShadows: customShadows('light'),
      typography,
      shape: { borderRadius: 8 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '*': {
              boxSizing: 'border-box',
              margin: 0,
              padding: 0,
            },
            html: {
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              scrollBehavior: 'smooth',
            },
            body: {
              minHeight: '100vh',
              overflowX: 'hidden',
            },
            '#root': {
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        },
        MuiContainer: {
          styleOverrides: {
            root: {
              paddingLeft: { xs: 2, sm: 3, md: 4 },
              paddingRight: { xs: 2, sm: 3, md: 4 },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              boxShadow: 'none',
              position: 'relative',
              zIndex: 0,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
              fontWeight: 600,
              padding: '8px 16px',
              fontSize: '0.875rem',
              '@media (max-width: 600px)': {
                padding: '6px 12px',
                fontSize: '0.75rem',
              },
            },
          },
        },
      },
    };

    return merge(
      baseOption,
      directionOption,
      darkModeOption,
      presetsOption,
      contrastOption.theme
    );
  }, [
    settings.themeMode,
    settings.themeColorPresets,
    settings.themeContrast,
    settings.themeDirection,
  ]);

  // Create theme with memoized options
  const theme = useMemo(() => createTheme(themeOptions), [themeOptions]);

  // Apply component overrides
  theme.components = merge(
    componentsOverrides(theme),
    contrast(settings.themeContrast === 'bold', settings.themeMode).components
  );

  // Create theme with locale
  const themeWithLocale = useMemo(
    () => createTheme(theme, currentLang.systemValue),
    [currentLang.systemValue, theme]
  );

  return (
    <MuiThemeProvider theme={themeWithLocale}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
};
